
import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, User, Package, Calendar, Image as ImageIcon, Heart, ChevronDown } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
// Image removed - use img tag;
import { Link } from "react-router-dom";

interface WishlistItem {
  id: number;
  user_id: number;
  product_id: number;
  user: {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
  product: {
    id: number;
    name: string;
    slug: string;
    price: number;
    original_price: number | null;
    image_url: string | null;
  } | null;
  created_at: string | null;
}

interface GroupedWishlist {
  user: {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  };
  items: WishlistItem[];
  totalItems: number;
}

export default function AdminWishlistPage() {
  const { token } = useAuthStore();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (token) {
      fetchWishlistItems();
    }
  }, [token]);

  const fetchWishlistItems = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getWishlistItems(token!);
      // Backend wraps response in: { success, message, data: { total, wishlist_items } }
      const wrappedResponse = response;
      let itemsList: WishlistItem[] = [];
      
      if (wrappedResponse.data) {
        itemsList = wrappedResponse.data.wishlist_items || wrappedResponse.data || [];
      } else if (wrappedResponse.wishlist_items) {
        itemsList = wrappedResponse.wishlist_items;
      } else if (Array.isArray(wrappedResponse)) {
        itemsList = wrappedResponse;
      }
      
      setWishlistItems(itemsList);
    } catch (error: any) {
      console.error("Error fetching wishlist items:", error);
      toast.error(error.response?.data?.detail || "Failed to load wishlist items");
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Group wishlist items by user
  const groupedWishlist = useMemo(() => {
    const grouped = new Map<number, GroupedWishlist>();
    
    wishlistItems.forEach((item) => {
      if (!item.user) return; // Skip items without user
      
      const userId = item.user.id;
      if (!grouped.has(userId)) {
        grouped.set(userId, {
          user: item.user,
          items: [],
          totalItems: 0,
        });
      }
      
      const userGroup = grouped.get(userId)!;
      userGroup.items.push(item);
      userGroup.totalItems = userGroup.items.length;
    });
    
    return Array.from(grouped.values());
  }, [wishlistItems]);

  // Filter grouped wishlist based on search term
  const filteredGroupedWishlist = useMemo(() => {
    if (!searchTerm) return groupedWishlist;
    
    const searchLower = searchTerm.toLowerCase();
    return groupedWishlist
      .map((group) => {
        // Check if user matches search
        const userMatches =
          group.user.email?.toLowerCase().includes(searchLower) ||
          group.user.first_name?.toLowerCase().includes(searchLower) ||
          group.user.last_name?.toLowerCase().includes(searchLower);
        
        // Filter items that match search
        const filteredItems = group.items.filter(
          (item) =>
            item.product?.name?.toLowerCase().includes(searchLower) ||
            userMatches
        );
        
        // Return group only if it has matching items or user matches
        if (filteredItems.length > 0 || userMatches) {
          return {
            ...group,
            items: userMatches ? group.items : filteredItems,
          };
        }
        return null;
      })
      .filter((group): group is GroupedWishlist => group !== null);
  }, [groupedWishlist, searchTerm]);

  // All sections are collapsed by default - no initialization needed

  const toggleUser = (userId: number) => {
    setExpandedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const isExpanded = (userId: number) => expandedUsers.has(userId);

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0">
            <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-1" />
            <div className="h-3 w-48 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <div className="w-1/2 h-9 bg-gray-200 rounded-full animate-pulse" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
                  <div>
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-1" />
                    <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="space-y-2">
                {[1, 2].map((j) => (
                  <div key={j} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="flex-1">
                      <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mb-1" />
                      <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header, Search in one row */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-shrink-0">
          <h1 className="text-xl font-bold font-playfair mb-0.5">Wishlist</h1>
          <p className="text-xs text-gray-500">View wishlist items grouped by user</p>
        </div>
        <div className="w-1/2 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by product name or user..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Grouped Wishlist Items */}
      {filteredGroupedWishlist.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {searchTerm ? "No wishlist items found matching your search" : "No wishlist items found"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredGroupedWishlist.map((group) => (
            <motion.div
              key={group.user.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* User Header - Clickable */}
              <button
                onClick={() => toggleUser(group.user.id)}
                className="w-full bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 px-4 py-3 hover:from-purple-100 hover:to-pink-100 transition-all text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                      {group.user.first_name?.[0]?.toUpperCase() || group.user.email?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {group.user.first_name || ""} {group.user.last_name || ""}
                        {!group.user.first_name && !group.user.last_name && "Unknown User"}
                      </p>
                      <p className="text-xs text-gray-500">{group.user.email || "No email"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-pink-500" />
                      <span className="text-xs font-semibold text-gray-700">
                        {group.totalItems} {group.totalItems === 1 ? "item" : "items"}
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: isExpanded(group.user.id) ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    </motion.div>
                  </div>
                </div>
              </button>

              {/* User's Wishlist Items - Collapsible */}
              <AnimatePresence>
                {isExpanded(group.user.id) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="divide-y divide-gray-100">
                {group.items.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-gray-500">
                    No items match your search
                  </div>
                ) : (
                  group.items.map((item) => (
                    <div
                      key={item.id}
                      className="px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {/* Product Image */}
                        {item.product?.image_url ? (
                          <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img
                              src={item.product.image_url}
                              alt={item.product.name || "Product"}
                              
                              className="object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="w-6 h-6 text-gray-400" />
                          </div>
                        )}

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              {item.product ? (
                                <Link
                                  to={`/products/${item.product.slug}`}
                                  className="font-semibold text-sm text-gray-900 hover:text-purple-600 transition-colors block truncate"
                                >
                                  {item.product.name || "Unknown Product"}
                                </Link>
                              ) : (
                                <p className="font-semibold text-sm text-gray-500">Product Deleted</p>
                              )}
                              {item.product?.slug && (
                                <p className="text-xs text-gray-500 truncate">/{item.product.slug}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-500">
                                  {item.created_at
                                    ? new Date(item.created_at).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })
                                    : "—"}
                                </span>
                              </div>
                            </div>

                            {/* Price */}
                            <div className="text-right flex-shrink-0">
                              {item.product ? (
                                <div>
                                  <p className="font-semibold text-sm text-gray-900">
                                    ₹{item.product.price?.toLocaleString() || "0"}
                                  </p>
                                  {item.product.original_price &&
                                    item.product.original_price > item.product.price && (
                                      <p className="text-xs text-gray-500 line-through">
                                        ₹{item.product.original_price.toLocaleString()}
                                      </p>
                                    )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
